/**
 * Library data model + mock seed.
 *
 * Mirrors the iOS `LibraryViewModel` published state (`LibraryProfile`,
 * `Metric`, `Track`, `Playlist`, `Thread`) so the web Library page renders a
 * faithful personal-hub layout for terminal users without a live backend.
 * All colors resolve through DS tokens at render time; no color literals
 * live in this module.
 */

import type { ArtistIconName } from "@/components/design-system/ArtistDesignSystem";

export interface LibraryProfile {
	readonly id: string;
	readonly displayName: string;
	readonly tierLabel: string | null;
	readonly demographic: string;
	readonly location: string;
	readonly followingCount: number;
	readonly fansCount: number;
	readonly likesCount: number;
	readonly heroImageURL: string | null;
	readonly avatarURL: string | null;
	readonly welcomeMessage: string;
	readonly bio: string | null;
}

export interface LibraryMetric {
	readonly id: string;
	readonly title: string;
	readonly value: string;
	readonly caption: string;
	readonly iconName: ArtistIconName;
}

export interface LibraryTrack {
	readonly id: string;
	readonly title: string;
	readonly artworkURL: string | null;
	readonly likeCount: number;
	readonly isLiked: boolean;
}

export interface LibraryPlaylist {
	readonly id: string;
	readonly name: string;
	readonly coverImageURL: string | null;
	readonly trackCount: number;
	readonly isSystemPlaylist: boolean;
	readonly isDefaultFavorites: boolean;
}

export interface LibraryNote {
	readonly id: string;
	readonly content: string;
	readonly timestamp: string;
	readonly isLocal: boolean;
	readonly mediaURLs: readonly string[];
}

export interface LibraryData {
	readonly profile: LibraryProfile;
	readonly metrics: readonly LibraryMetric[];
	readonly works: readonly LibraryTrack[];
	readonly liked: readonly LibraryTrack[];
	readonly playlists: readonly LibraryPlaylist[];
	readonly notes: readonly LibraryNote[];
}

function metrics(locked: boolean): readonly LibraryMetric[] {
	const value = locked ? "--" : "0";
	const caption = locked ? "登录后查看" : "暂无数据";
	return [
		{ id: "metric-listening-time", title: "本周收听", value, caption, iconName: "waveform" },
		{ id: "metric-fans", title: "粉丝", value, caption, iconName: "person" },
		{ id: "metric-total-likes", title: "累计获赞", value, caption, iconName: "heart" },
		{ id: "metric-avg-likes", title: "单作品均赞", value, caption, iconName: "sparkle" },
		{ id: "metric-total-shares", title: "累计分享", value, caption, iconName: "share" },
	];
}

export function buildUserLibraryData(
	userId: string,
	displayName: string,
	bio: string | null,
): LibraryData {
	return {
		profile: {
			id: userId,
			displayName,
			tierLabel: null,
			demographic: "",
			location: "",
			followingCount: 0,
			fansCount: 0,
			likesCount: 0,
			heroImageURL: null,
			avatarURL: null,
			welcomeMessage: "欢迎回到你的创作空间。",
			bio,
		},
		metrics: metrics(false),
		works: [],
		liked: [],
		playlists: [],
		notes: [],
	};
}

export function buildPlaceholderLibraryData(): LibraryData {
	return {
		profile: {
			id: "placeholder",
			displayName: "",
			tierLabel: null,
			demographic: "",
			location: "",
			followingCount: 0,
			fansCount: 0,
			likesCount: 0,
			heroImageURL: null,
			avatarURL: null,
			welcomeMessage: "登录后开启你的创作空间。",
			bio: null,
		},
		metrics: metrics(true),
		works: [],
		liked: [],
		playlists: [],
		notes: [],
	};
}

export const LIBRARY_TAB_IDS = [
	"works",
	"notes",
	"collections",
	"liked",
] as const;
export type LibraryTabId = (typeof LIBRARY_TAB_IDS)[number];

export const LIBRARY_TAB_LABELS: Readonly<Record<LibraryTabId, string>> = {
	works: "作品",
	notes: "笔记",
	collections: "收藏",
	liked: "好听",
};
